import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { TrackingInput } from 'src/app/core/services/tracking/models/tracking-object.interface';
import { TrackingService } from 'src/app/core/services/tracking/tracking.service';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { Route } from 'src/app/routing/route.enum';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { fromCarl } from 'src/app/store/common/common.selectors';
import { EffectsBase } from 'src/app/store/effects-base';
import { selectRouteState } from 'src/app/store/router/router.selectors';
import { noop } from 'src/app/utils/functions/noop';

import { ActivatedRouteState } from '../../services/activated-route/activated-route-state.interface';
import { TrackingAction } from '../../services/tracking/models/tracking-action.enum';
import { TrackingCategory } from '../../services/tracking/models/tracking-category.enum';
import { TrackingPlaceholder } from '../../services/tracking/models/tracking-placeholder.enum';
import { AppErrorMinor } from '../app-error-minor.enum';
import { AppError, AppErrorWithNotification } from '../app-error.interface';

import { AppErrorActionType, appErrorMinor } from './app-error.actions';

type HttpError = { error?: Error & { status: number } } & Omit<HttpErrorResponse, 'error'>;

@Injectable()
export class AppErrorEffects extends EffectsBase {
  public showMinorLoadingErrorNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppErrorActionType.AppErrorMinorLoading),
      concatLatestFrom(() => this.store.select<string>(fromCarl.selectTranslation('error.minor.general.load'))),
      map(([{ error }, translation]: [{ error: HttpError }, string]) => {
        const loadingIssue: AppErrorWithNotification = {
          statusCode: error.error?.status,
          message: error.error?.message,
          name: error.error?.name,
          type: AppErrorMinor.LoadingIssue,
          notification: {
            message: translation,
            variant: 'error',
            dismissable: true,
            autoDismiss: true
          }
        };
        return appErrorMinor({ errorWithNotification: loadingIssue });
      })
    )
  );

  /**
   * if an major error occurs, route to error page
   */
  public routeToErrorPage$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(AppErrorActionType.AppErrorMajor),
        map(({ error }: { error: AppError }) => {
          const trackingInput: TrackingInput = {
            routeState: AppRoutePath.ErrorPage,
            clickData: {
              category: TrackingCategory.Impression,
              action: TrackingAction.ConfiguratorError,
              label: `${error.name}_${error.statusCode}_${error.type}`
            },
            errorData: {
              errorType: error.statusCode || TrackingPlaceholder.ErrorTypeUndefined,
              errorDescription: `${error.name}_${error.type}`
            }
          };
          this.trackingService.sendTrackingData(trackingInput);
          this.router.navigate([Route.ErrorPage], { skipLocationChange: true }).catch(noop);
        })
      ),
    { dispatch: false }
  );

  /**
   * if a minor error occurs, a notification box is displayed when the payload contains a message, otherwise also route to error page
   */
  public displayNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppErrorActionType.AppErrorMinor),
        concatLatestFrom(() => this.store.select(selectRouteState).pipe(first())),
        map(
          ([{ errorWithNotification }, activatedRouteState]: [
            { errorWithNotification: AppErrorWithNotification },
            ActivatedRouteState | undefined
          ]): void => {
            if (activatedRouteState?.appRoutePath !== AppRoutePath.ErrorPage) {
              const trackingInput: TrackingInput = {
                routeState: activatedRouteState?.appRoutePath,
                clickData: {
                  category: TrackingCategory.Impression,
                  action: TrackingAction.ConfiguratorError,
                  label: `${errorWithNotification.name}_${errorWithNotification.statusCode}_${errorWithNotification.type}`
                },
                errorData: {
                  errorType: errorWithNotification.statusCode || TrackingPlaceholder.ErrorTypeUndefined,
                  errorDescription: errorWithNotification.name || TrackingPlaceholder.ErrorDescriptionUndefined
                }
              };

              this.trackingService.sendTrackingData(trackingInput);
            }

            if (errorWithNotification.notification) {
              this.notificationService.notify({
                content: errorWithNotification.notification.message || 'N/A',
                variant: errorWithNotification.notification.variant || 'error',
                lifetime:
                  typeof errorWithNotification.notification.lifetime === 'number' &&
                  errorWithNotification.notification.lifetime > 0
                    ? errorWithNotification.notification.lifetime
                    : undefined,
                dismissable: !!errorWithNotification.notification.dismissable
              });
            } else {
              this.router.navigate([Route.ErrorPage], { skipLocationChange: true }).catch(noop);
            }
          }
        )
      ),
    { dispatch: false }
  );

  public constructor(
    private readonly router: Router,
    private readonly trackingService: TrackingService,
    private readonly notificationService: NotificationService,
    private readonly store: Store
  ) {
    super();
  }
}
